import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { authAPI, notificationsAPI, systemConfigAPI } from '../services/api';
import {
  HomeIcon,
  ScaleIcon,
  TruckIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  CubeIcon,
  CreditCardIcon,
  ChartBarIcon,
  ChartPieIcon,
  Bars3Icon,
  XMarkIcon,
  MapPinIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import FcfaIcon from './FcfaIcon';
import { getUserRoleDisplay } from '../utils/helpers';
import LanguageSwitcher from './LanguageSwitcher';
import Modal from './Modal';
import Input from './Input';
import Button from './Button';
import { useToast } from '../contexts/ToastContext';
import { APP_BUILD_VERSION } from '../generated/appBuildVersion';

// Custom truck dump icon using image
const TruckDumpIcon = ({ className }) => (
  <img 
    src="/truckdump.png" 
    alt="Truck dump" 
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

// Custom certificate icon using image
const CertificateIcon = ({ className }) => (
  <img 
    src="/certificates.png" 
    alt="Certificate" 
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

// Custom weigh scale icon using image
const WeighScaleIcon = ({ className }) => (
  <img 
    src="/weighscale.png" 
    alt="Weigh scale" 
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const notificationPanelRef = useRef(null);
  const activeDesktopNavItemRef = useRef(null);
  const activeMobileNavItemRef = useRef(null);
  const { user, logout, changePassword, isManagerOrAdmin, isOperator, isDriver, isPeseur, isAccountant, isSuper, getOperatorClient, getDriverTruck, getDriverTrucks } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if tracking module is enabled
  const { data: trackingConfig } = useApi(
    () => systemConfigAPI.getByKey('module.tracking.enabled'),
    [],
    { showErrorToast: false, immediate: true }
  );

  const isTrackingEnabled = trackingConfig?.value === true || trackingConfig?.value === 'true';

  const supportsNotifications =
    user?.role === 'MANAGER' ||
    user?.role === 'CLIENT' ||
    user?.role === 'PESEUR' ||
    user?.role === 'ACCOUNTANT';

  const fetchNotifications = useCallback(async () => {
    if (!supportsNotifications) return;
    try {
      setNotificationsLoading(true);
      const data = await notificationsAPI.getMine();
      setNotifications(Array.isArray(data?.notifications) ? data.notifications : []);
      setUnreadNotificationCount(Number(data?.unreadCount || 0));
    } catch (_error) {
      // Ignore notification fetch errors to avoid disrupting the shell.
    } finally {
      setNotificationsLoading(false);
    }
  }, [supportsNotifications]);

  useEffect(() => {
    if (!supportsNotifications) return undefined;
    fetchNotifications();
    const intervalId = window.setInterval(fetchNotifications, 30000);
    return () => window.clearInterval(intervalId);
  }, [supportsNotifications, fetchNotifications]);

  useEffect(() => {
    if (!notificationOpen) return undefined;

    const handlePointerDown = (event) => {
      if (notificationPanelRef.current && !notificationPanelRef.current.contains(event.target)) {
        setNotificationOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [notificationOpen]);

  useEffect(() => {
    const scrollActiveNavItemIntoView = () => {
      activeDesktopNavItemRef.current?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: 'smooth'
      });

      if (sidebarOpen) {
        activeMobileNavItemRef.current?.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
          behavior: 'smooth'
        });
      }
    };

    const frameId = window.requestAnimationFrame(scrollActiveNavItemIntoView);
    return () => window.cancelAnimationFrame(frameId);
  }, [location.pathname, sidebarOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNotificationClick = useCallback(async (notification) => {
    if (!notification) return;

    if (notification.kind === 'stored' && !notification.read) {
      try {
        await notificationsAPI.markRead(notification.id);
        setNotifications((prev) => prev.filter((item) => item.id !== notification.id));
        setUnreadNotificationCount((prev) => Math.max(0, prev - 1));
      } catch (_error) {
        // Ignore mark-as-read failures and continue navigation.
      }
    }

    setNotificationOpen(false);
    const adminNoNavRoutes = ['/bills', '/payments', '/loads', '/certificates', '/ddp/list', '/requestedloads'];
    const adminBlockedDdp =
      notification.route?.startsWith('/ddp/') && notification.route !== '/ddp/create';
    if (notification.route && user?.role === 'ADMIN' && (adminNoNavRoutes.includes(notification.route) || adminBlockedDdp)) {
      return;
    }
    if (notification.route) {
      let navigationState;
      if (notification.route === '/certificates' && notification.entityId) {
        navigationState = {
          openCertificateId: notification.entityId,
          openCertificateAction:
            notification.type === 'CERTIFICATE_PENDING_CERTIFICATION' ? 'review' : 'view'
        };
      } else if (notification.route === '/bills' && notification.entityId) {
        navigationState = { openBillId: notification.entityId };
      } else if (notification.route === '/payments' && notification.entityId) {
        navigationState = { openPaymentId: notification.entityId };
      }
      navigate(notification.route, navigationState ? { state: navigationState } : undefined);
    }
  }, [navigate, user?.role]);

  const handleDismissNotification = useCallback(async (event, notification) => {
    event.stopPropagation();
    if (!notification) return;

    try {
      if (notification.kind === 'live') {
        if (!notification.entityId) return;
        await notificationsAPI.dismissLive(notification.type, notification.entityId);
      } else {
        await notificationsAPI.dismiss(notification.id);
      }
      setNotifications((prev) => prev.filter((item) => item.id !== notification.id));
      setUnreadNotificationCount((prev) => Math.max(0, prev - (notification.read ? 0 : 1)));
    } catch (_error) {
      // Ignore dismiss failures to keep menu usable.
    }
  }, []);

  const navigation = [
    { name: isSuper() ? t('navigation.financialDashboard') : t('navigation.dashboard'), href: isSuper() ? '/super-dashboard' : '/dashboard', icon: HomeIcon, current: location.pathname === '/dashboard' || location.pathname === '/super-dashboard' },
    
    // Demandes de Pesée (DDP) – Manager/Peseur list; Client sees "Mes demandes..." (admins use /ddp/create only)
    ...((user?.role === 'CLIENT' || user?.role === 'MANAGER' || user?.role === 'PESEUR') ? [
      {
        name:
          user?.role === 'MANAGER'
            ? t('navigation.ddpListAdmin')
            : user?.role === 'PESEUR'
              ? t('navigation.ddpPeseur')
              : t('navigation.ddpList'),
        href: '/ddp/list',
        icon: WeighScaleIcon,
        current: location.pathname === '/ddp/list'
      },
    ] : []),

    // Pesages (loads) and Certificates
    ...(isDriver() ? [
      { name: t('navigation.myLoads'), href: '/loads', icon: TruckDumpIcon, current: location.pathname === '/loads' },
      { name: t('navigation.myTrucks'), href: '/driver/trucks', icon: TruckIcon, current: location.pathname === '/driver/trucks' },
      // GPS Location - only if tracking module is enabled
      ...(isTrackingEnabled ? [
        { name: t('navigation.gps'), href: '/gps', icon: MapPinIcon, current: location.pathname === '/gps' },
      ] : []),
    ] : isAccountant() ? [
      // Accountants only see certificates, not loads
      { name: t('navigation.certificates'), href: '/certificates', icon: CertificateIcon, current: location.pathname === '/certificates' },
    ] : isSuper() ? [
      // SUPER users don't see loads or certificates - they focus on financial reports
    ] : isPeseur() ? [
      // Peseur (weigher) sees loads, certificates, and their weighing reports
      { name: t('navigation.loads'), href: '/loads', icon: TruckDumpIcon, current: location.pathname === '/loads' },
      { name: t('navigation.certificates'), href: '/certificates', icon: CertificateIcon, current: location.pathname === '/certificates' },
      { name: t('navigation.reports'), href: '/reports', icon: ChartBarIcon, current: location.pathname === '/reports' },
    ] : user?.role === 'ADMIN' ? [
      // Admins: system/financial tools only (no operational loads / certificates / DDP list)
    ] : [
      // MANAGER, CLIENT: loads and certificates
      { name: t('navigation.loads'), href: '/loads', icon: TruckDumpIcon, current: location.pathname === '/loads' },
      { name: t('navigation.certificates'), href: '/certificates', icon: CertificateIcon, current: location.pathname === '/certificates' },
    ]),
    
    // GPS Tracking - available for clients, managers, and admins (not SUPER, not Peseur) - only if module is enabled
    ...(!isDriver() && !isSuper() && !isPeseur() && isTrackingEnabled ? [
      { name: t('navigation.tracking'), href: '/tracking', icon: MapPinIcon, current: location.pathname === '/tracking', roles: ['ADMIN', 'MANAGER', 'CLIENT'] },
    ] : []),
  ];

  const managementNav = [
    { name: user?.role === 'CLIENT' ? t('navigation.myProfile') : t('navigation.clients'), href: '/clients', icon: BuildingOfficeIcon, current: location.pathname === '/clients', roles: ['ADMIN', 'MANAGER', 'CLIENT', 'PESEUR', 'ACCOUNTANT'] },
    {
      name: t('navigation.transporteurs'),
      icon: TruckIcon,
      current: location.pathname.startsWith('/transporteurs'),
      roles: ['ADMIN', 'MANAGER', 'CLIENT'],
      children: [
        { name: t('navigation.transporteursCarriers'), href: '/transporteurs', current: location.pathname === '/transporteurs', roles: ['ADMIN', 'MANAGER', 'CLIENT'] },
        { name: t('navigation.drivers'), href: '/transporteurs/drivers', current: location.pathname.startsWith('/transporteurs/drivers'), roles: ['ADMIN', 'MANAGER', 'CLIENT', 'PESEUR'] },
        { name: t('navigation.trucks'), href: '/transporteurs/trucks', current: location.pathname.startsWith('/transporteurs/trucks'), roles: ['ADMIN', 'MANAGER', 'CLIENT', 'PESEUR'] },
      ],
    },
    { name: t('navigation.products'), href: '/products', icon: CubeIcon, current: location.pathname === '/products', roles: ['ADMIN', 'MANAGER'] },
    { name: t('navigation.ports'), href: '/ports', icon: BuildingOfficeIcon, current: location.pathname === '/ports', roles: ['ADMIN'] },
    { name: t('navigation.moles'), href: '/moles', icon: MapPinIcon, current: location.pathname === '/moles', roles: ['ADMIN'] },
    { name: t('navigation.weighbridges'), href: '/weighbridges', icon: ScaleIcon, current: location.pathname === '/weighbridges', roles: ['ADMIN', 'MANAGER'] }
  ];

  const financialNav = [
    { name: t('navigation.bills'), href: '/bills', icon: FcfaIcon, current: location.pathname === '/bills', roles: ['MANAGER', 'ACCOUNTANT', 'CLIENT', 'SUPER', 'PESEUR'] },
    { name: t('navigation.payments'), href: '/payments', icon: CreditCardIcon, current: location.pathname === '/payments', roles: ['MANAGER', 'ACCOUNTANT', 'CLIENT', 'SUPER'] },
  ];

  const systemNav = [
    { name: t('navigation.reports'), href: '/reports', icon: ChartBarIcon, current: location.pathname === '/reports', roles: ['ADMIN', 'MANAGER'] },
    // Show "Users" for admins
    ...(user?.role === 'ADMIN' ? [
      { name: t('navigation.users'), href: '/users', icon: UserGroupIcon, current: location.pathname === '/users', roles: ['ADMIN'] },
      { name: t('navigation.errorLogs'), href: '/error-logs', icon: ExclamationTriangleIcon, current: location.pathname === '/error-logs', roles: ['ADMIN'] },
      { name: t('navigation.adminStats'), href: '/admin-stats', icon: ChartPieIcon, current: location.pathname === '/admin-stats', roles: ['ADMIN'] },
      { name: t('navigation.systemConfig'), href: '/system-config', icon: Cog6ToothIcon, current: location.pathname === '/system-config', roles: ['ADMIN'] }
    ] : [])
  ];

  const hasAccess = (item) => {
    if (!item.roles) return true;
    return item.roles.includes(user?.role);
  };

  const getAccessibleItems = (items) => {
    return items.filter(hasAccess);
  };

  const NavSection = ({ title, items, mobile = false }) => {
    const accessibleItems = getAccessibleItems(items);
    // Don't render the section if there are no accessible items
    if (accessibleItems.length === 0) {
      return null;
    }

    const renderNavItem = (item) => {
      const activeItemRef = mobile ? activeMobileNavItemRef : activeDesktopNavItemRef;

      if (item.children) {
        const accessibleChildren = getAccessibleItems(item.children);
        if (accessibleChildren.length === 0) return null;
        return (
          <div key={item.name} className="space-y-0.5">
            <div
              className={`
                flex items-center px-2 py-2 text-sm font-medium rounded-md
                ${item.current ? 'text-blue-900' : 'text-gray-700'}
              `}
            >
              <item.icon className="mr-3 h-5 w-5 flex-shrink-0 text-gray-500" />
              {item.name}
            </div>
            {accessibleChildren.map((child) => (
              <Link
                key={child.name}
                to={child.href}
                ref={child.current ? activeItemRef : null}
                className={`
                  group flex items-center pl-10 pr-2 py-2 text-sm font-medium rounded-md
                  ${child.current
                    ? 'bg-blue-100 text-blue-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
                onClick={() => setSidebarOpen(false)}
              >
                {child.name}
              </Link>
            ))}
          </div>
        );
      }
      if (typeof item.onClick === 'function') {
        return (
          <button
            key={item.name}
            type="button"
            className="group flex items-center w-full text-left px-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            onClick={() => {
              setSidebarOpen(false);
              item.onClick();
            }}
          >
            <item.icon className="mr-3 h-5 w-5 flex-shrink-0 text-gray-500" />
            {item.name}
          </button>
        );
      }
      return (
        <Link
          key={item.name}
          to={item.href}
          ref={item.current ? activeItemRef : null}
          className={`
            group flex items-center px-2 py-2 text-sm font-medium rounded-md
            ${item.current
              ? 'bg-blue-100 text-blue-900'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }
          `}
          onClick={() => setSidebarOpen(false)}
        >
          <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
          {item.name}
        </Link>
      );
    };

    return (
      <div className="space-y-1">
        <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {title}
        </h3>
        {accessibleItems.map(renderNavItem)}
      </div>
    );
  };

  const Sidebar = ({ mobile = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center h-16 px-4 bg-blue-600">
        <div className="flex items-center">
          <img 
            src="/logo_cciad.jpg" 
            alt="CCIAD Logo" 
            className="h-8 w-8 object-contain"
          />
          <span className="ml-2 text-white font-bold text-lg">iPortCloud</span>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
        <div className="flex-1 px-3 space-y-6">
          <NavSection title={t('sections.operations')} items={navigation} mobile={mobile} />
          {(isManagerOrAdmin() || isOperator() || isPeseur() || isAccountant()) && <NavSection title={t('sections.management')} items={managementNav} mobile={mobile} />}
          {(isManagerOrAdmin() || isAccountant() || isOperator() || isSuper() || isPeseur()) && <NavSection title={t('sections.financial')} items={financialNav} mobile={mobile} />}
          {(isManagerOrAdmin() || isOperator()) && <NavSection title={t('sections.system')} items={systemNav} mobile={mobile} />}
        </div>
      </div>

      {/* User profile */}
      <div className="flex-shrink-0 flex border-t border-gray-200 p-3 sm:p-4">
        <button
          onClick={() => setIsChangePasswordModalOpen(true)}
          className="flex-1 group block min-w-0 text-left hover:bg-gray-50 rounded-md p-1 -m-1 transition-colors"
          title={t('profile.changePassword')}
        >
          <div className="flex items-center">
            <div className="inline-block h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
              <UserIcon className="h-5 w-5 text-gray-600" />
            </div>
            <div className="ml-2 sm:ml-3 min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-700 truncate">
                {user?.firstName} {user?.lastName}
                {isOperator() && getOperatorClient() && (
                  <span className="text-blue-600 font-normal hidden sm:inline"> • {getOperatorClient().companyName}</span>
                )}
                {isDriver() && getDriverTruck() && (
                  <span className="text-green-600 font-normal hidden sm:inline">
                    {' '}
                    • {getDriverTruck().plateNumber || getDriverTruck().id}
                    {getDriverTrucks().length > 1 ? ` (+${getDriverTrucks().length - 1})` : ''}
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {getUserRoleDisplay(user?.role)}
              </p>
            </div>
          </div>
        </button>
        <button
          onClick={handleLogout}
          className="ml-2 sm:ml-3 text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0"
          title={t('common.logout')}
        >
          <ArrowRightOnRectangleIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100 overflow-x-hidden">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <XMarkIcon className="h-6 w-6 text-white" />
              </button>
            </div>
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 bg-white shadow">
            <Sidebar />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top bar */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <button
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex items-center">
              <h1 className="text-2xl font-semibold text-gray-900">
                {(() => {
                  const allItems = navigation.concat(managementNav, financialNav, systemNav);
                  for (const item of allItems) {
                    if (item.children) {
                      const child = item.children.find(c => c.current);
                      if (child) return child.name;
                      if (item.current) return item.name;
                    } else if (item.current) return item.name;
                  }
                  return t('login.title');
                })()}
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              {/* Show client info in header */}
              {isOperator() && getOperatorClient() && (
                <div 
                  className="hidden sm:flex items-center text-sm text-gray-600"
                  title={`${t('weighing.workingFor')} ${getOperatorClient().companyName}`}
                >
                  <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                  <span>{getOperatorClient().companyName}</span>
                </div>
              )}
              
              {/* Show driver truck info in header */}
              {isDriver() && getDriverTruck() && (
                <div 
                  className="hidden sm:flex items-center text-sm text-gray-600"
                  title={getDriverTrucks().map((t) => t.plateNumber || t.id).join(', ')}
                >
                  <TruckIcon className="h-4 w-4 mr-1" />
                  <span>
                    {getDriverTruck().plateNumber || getDriverTruck().id}
                    {getDriverTrucks().length > 1 ? ` (+${getDriverTrucks().length - 1})` : ''}
                  </span>
                </div>
              )}
              {supportsNotifications && (
                <div className="relative" ref={notificationPanelRef}>
                  <button
                    type="button"
                    onClick={() => {
                      const nextOpen = !notificationOpen;
                      setNotificationOpen(nextOpen);
                      if (nextOpen) {
                        fetchNotifications();
                      }
                    }}
                    className="relative min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                    title={t('notifications.title')}
                  >
                    <BellIcon className="h-6 w-6" />
                    {unreadNotificationCount > 0 && (
                      <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-xs font-semibold">
                        {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                      </span>
                    )}
                  </button>

                  {notificationOpen && (
                    <div className="absolute right-0 mt-2 w-96 max-w-[90vw] bg-white border border-gray-200 rounded-lg shadow-lg z-30">
                      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-900">{t('notifications.title')}</h3>
                        <span className="text-xs text-gray-500">
                          {t('notifications.unreadCount', { count: unreadNotificationCount })}
                        </span>
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {notificationsLoading ? (
                          <div className="px-4 py-6 text-sm text-gray-500">{t('common.loading')}</div>
                        ) : notifications.length === 0 ? (
                          <div className="px-4 py-6 text-sm text-gray-500">{t('notifications.none')}</div>
                        ) : (
                          notifications.map((notification) => (
                            <button
                              key={notification.id}
                              type="button"
                              onClick={() => handleNotificationClick(notification)}
                              className={`w-full text-left px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 ${
                                notification.read ? 'bg-white' : 'bg-blue-50'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                                  <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                                  <p className="text-xs text-gray-400 mt-2">
                                    {new Date(notification.createdAt).toLocaleString()}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {!notification.read && (
                                    <span className="inline-flex h-2.5 w-2.5 rounded-full bg-blue-600 mt-1 flex-shrink-0" />
                                  )}
                                  {((notification.kind === 'live' && user?.role === 'MANAGER' && notification.entityId) || notification.kind === 'stored') && (
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onClick={(event) => handleDismissNotification(event, notification)}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                          handleDismissNotification(event, notification);
                                        }
                                      }}
                                      className="inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-200 flex-shrink-0"
                                      title={t('common.dismiss')}
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </span>
                                  )}
                                </div>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto overflow-x-hidden focus:outline-none">
          <div className="py-4 sm:py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 w-full min-w-0 overflow-x-hidden">
              {children}
            </div>
          </div>
          
          {/* Footer */}
          <footer className="bg-white border-t border-gray-200 py-4">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-x-5 gap-y-1 text-sm text-gray-500">
                <span
                  className="font-mono text-xs text-gray-400 tabular-nums"
                  title={t('common.buildVersionTitle')}
                >
                  {APP_BUILD_VERSION}
                </span>
                <span className="text-center">
                  © {new Date().getFullYear()} Toubanetwork - Tous droits réservés
                </span>
              </div>
            </div>
          </footer>
        </main>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
        onChangePassword={changePassword}
        username={user?.username}
        user={user}
      />

    </div>
  );
};

// Change Password Modal Component
const ChangePasswordModal = ({ isOpen, onClose, onChangePassword, username, user }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('access');
  const [accessUser, setAccessUser] = useState(user || null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const dedupeById = (items) => {
    const map = new Map();
    (items || []).forEach((item) => {
      if (item?.id) map.set(item.id, item);
    });
    return Array.from(map.values());
  };

  const accessPorts = dedupeById([
    ...(accessUser?.accountantPorts || []).map((entry) => entry?.port || entry),
    ...(accessUser?.accessiblePorts || []).map((entry) => entry?.port || entry),
    ...(accessUser?.managedPorts || []).map((entry) => entry?.port || entry),
    ...(accessUser?.peseurWeighbridges || []).map((entry) => entry?.weighbridge?.port).filter(Boolean)
  ]);

  const accessWeighbridges = dedupeById([
    ...(accessUser?.accountantWeighbridges || []).map((entry) => entry?.weighbridge || entry),
    ...(accessUser?.peseurWeighbridges || []).map((entry) => entry?.weighbridge || entry),
    ...(accessUser?.managedWeighbridges || []).map((entry) => entry?.weighbridge || entry)
  ]);
  const accessMoles = dedupeById([
    ...(accessUser?.accountantMoles || []).map((entry) => entry?.mole || entry),
    ...(accessUser?.accessibleMoles || []).map((entry) => entry?.mole || entry),
    ...(accessUser?.managedMoles || []).map((entry) => entry?.mole || entry),
    ...accessWeighbridges
      .map((wb) => wb?.mole)
      .filter(Boolean)
  ]);
  const managerPortNames = [
    ...(accessUser?.accessiblePorts || []).map((entry) => entry?.port?.name).filter(Boolean),
    ...(accessUser?.managedPorts || []).map((entry) => entry?.port?.name).filter(Boolean)
  ];
  const managerMoleNames = [
    ...(accessUser?.accessibleMoles || []).map((entry) => {
      const moleName = entry?.mole?.name;
      const portName = entry?.mole?.port?.name;
      return moleName ? `${moleName}${portName ? ` (${portName})` : ''}` : null;
    }).filter(Boolean),
    ...(accessUser?.managedMoles || []).map((entry) => {
      const moleName = entry?.mole?.name;
      const portName = entry?.mole?.port?.name;
      return moleName ? `${moleName}${portName ? ` (${portName})` : ''}` : null;
    }).filter(Boolean)
  ];
  const accountantPortNames = (accessUser?.accountantPorts || [])
    .map((entry) => entry?.port?.name)
    .filter(Boolean);
  const accountantMoleNames = (accessUser?.accountantMoles || [])
    .map((entry) => {
      const moleName = entry?.mole?.name;
      const portName = entry?.mole?.port?.name;
      return moleName ? `${moleName}${portName ? ` (${portName})` : ''}` : null;
    })
    .filter(Boolean);
  const dedupedManagerPortNames = [...new Set(managerPortNames)];
  const dedupedManagerMoleNames = [...new Set(managerMoleNames)];
  const dedupedAccountantPortNames = [...new Set(accountantPortNames)];
  const dedupedAccountantMoleNames = [...new Set(accountantMoleNames)];
  const formattedCreatedAt = accessUser?.createdAt
    ? new Date(accessUser.createdAt).toLocaleDateString()
    : '-';
  const accessRowBaseClass = 'rounded-md border px-3 py-2';
  const accessPortsRowClass = `${accessRowBaseClass} border-blue-200 bg-blue-100/70`;
  const accessMolesRowClass = `${accessRowBaseClass} border-emerald-200 bg-emerald-100/70`;
  const accessWeighbridgesRowClass = `${accessRowBaseClass} border-violet-200 bg-violet-100/70`;
  const accessDriverRowClass = `${accessRowBaseClass} border-cyan-200 bg-cyan-100/70`;
  const accessClientRowClass = `${accessRowBaseClass} border-teal-200 bg-teal-100/70`;
  const accessAdminRowClass = `${accessRowBaseClass} border-indigo-200 bg-indigo-100/70`;
  const renderAccessValue = (values, emptyLabel) => {
    const cleaned = (values || []).filter(Boolean);
    if (cleaned.length === 0) return emptyLabel;
    if (cleaned.length === 1) return cleaned[0];
    return (
      <ul className="mt-1 list-disc list-inside space-y-0.5">
        {cleaned.map((value, idx) => (
          <li key={`${value}-${idx}`}>{value}</li>
        ))}
      </ul>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError(t('profile.allFieldsRequired'));
      return;
    }

    if (newPassword.length < 6) {
      setError(t('users.form.passwordMinLength'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('users.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);
    try {
      const result = await onChangePassword({ currentPassword, newPassword });
      if (result.success) {
        toast.success(t('profile.passwordChangedSuccessfully'));
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        onClose();
      } else {
        setError(result.error || t('profile.passwordChangeFailed'));
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || t('profile.passwordChangeFailed');
      // Translate common error messages
      if (errorMessage === 'Current password is incorrect' || errorMessage.includes('Current password is incorrect')) {
        setError(t('profile.currentPasswordIncorrect'));
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('access');
      setAccessUser(user || null);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    authAPI
      .getMe()
      .then((res) => {
        if (!cancelled && res?.data?.user) {
          setAccessUser(res.data.user);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('profile.myInformation')}
    >
      <div className="space-y-4">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex gap-6">
            <button
              type="button"
              onClick={() => setActiveTab('access')}
              className={`py-2 text-sm font-medium border-b-2 ${
                activeTab === 'access'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('profile.accessInfo')}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('password')}
              className={`py-2 text-sm font-medium border-b-2 ${
                activeTab === 'password'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('profile.changePassword')}
            </button>
          </nav>
        </div>

        {activeTab === 'access' ? (
          <div className="space-y-4">
            {username != null && username !== '' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('login.username')}
                </label>
                <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
                  {username}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <p className="text-sm font-medium text-gray-700">{t('users.table.role')}</p>
                <p className="text-sm text-gray-900">
                  {accessUser?.role ? t(`roles.${accessUser.role}`) : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">{t('users.table.status')}</p>
                <p className="text-sm text-gray-900">
                  {accessUser?.status ? t(`users.status.${accessUser.status}`) : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">{t('users.table.created')}</p>
                <p className="text-sm text-gray-900">{formattedCreatedAt}</p>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-800 mb-2">
                {t('users.accessControl')}
              </h4>
              <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-3 text-sm text-gray-800 space-y-2">
                {accessUser?.role === 'MANAGER' && (
                  <>
                    <div className="flex items-center mb-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        accessUser?.hasLimitedAccess ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {accessUser?.hasLimitedAccess ? t('users.limitedAccess') : t('users.fullAccess')}
                      </span>
                    </div>
                    <p className={accessPortsRowClass}>
                      <span className="font-medium">
                        {t('users.accessiblePorts')}:&nbsp;
                      </span>
                      {renderAccessValue(
                        dedupedManagerPortNames,
                        t('users.noneAssigned')
                      )}
                    </p>
                    <p className={accessMolesRowClass}>
                      <span className="font-medium">
                        {t('users.accessibleMoles')}:&nbsp;
                      </span>
                      {dedupedManagerMoleNames.length > 0
                        ? renderAccessValue(
                          dedupedManagerMoleNames,
                          t('users.noneAssigned')
                        )
                        : dedupedManagerPortNames.length > 0
                          ? t('users.allMolesInPorts')
                          : t('users.noneAssigned')}
                    </p>
                  </>
                )}
                {accessUser?.role === 'ACCOUNTANT' && (
                  <>
                    <p className={accessPortsRowClass}>
                      <span className="font-medium">
                        {t('users.accessiblePorts')}:&nbsp;
                      </span>
                      {renderAccessValue(
                        dedupedAccountantPortNames,
                        t('users.noneAssigned')
                      )}
                    </p>
                    <p className={accessMolesRowClass}>
                      <span className="font-medium">
                        {t('users.accessibleMoles')}:&nbsp;
                      </span>
                      {dedupedAccountantMoleNames.length > 0
                        ? renderAccessValue(
                          dedupedAccountantMoleNames,
                          t('users.noneAssigned')
                        )
                        : dedupedAccountantPortNames.length > 0
                          ? t('users.allMolesInPorts')
                          : t('users.noneAssigned')}
                    </p>
                  </>
                )}
                {accessUser?.role === 'PESEUR' && (
                  <>
                    <p className={accessWeighbridgesRowClass}>
                      <span className="font-medium">
                        {t('navigation.weighbridges')}:&nbsp;
                      </span>
                      {renderAccessValue(
                        accessWeighbridges.map((wb) => [wb.name, wb.location].filter(Boolean).join(' - ') || wb.id),
                        t('users.noneAssigned')
                      )}
                    </p>
                    <p className={accessPortsRowClass}>
                      <span className="font-medium">
                        {t('users.accessiblePorts')}:&nbsp;
                      </span>
                      {renderAccessValue(
                        accessPorts.map((port) => port.name || port.id),
                        t('users.noneAssigned')
                      )}
                    </p>
                    <p className={accessMolesRowClass}>
                      <span className="font-medium">
                        {t('users.accessibleMoles')}:&nbsp;
                      </span>
                      {accessMoles.length > 0
                        ? renderAccessValue(
                          accessMoles.map((mole) => {
                            const portName = mole?.port?.name ? ` (${mole.port.name})` : '';
                            return `${mole.name || mole.id}${portName}`;
                          }),
                          t('users.noneAssigned')
                        )
                        : t('users.noneAssigned')}
                    </p>
                  </>
                )}
                {accessUser?.role === 'DRIVER' && (
                  <p className={accessDriverRowClass}>
                    <span className="font-medium">
                      {t('users.assignedTruck')}:&nbsp;
                    </span>
                    {(accessUser?.assignedTrucks || []).length > 0
                      ? accessUser.assignedTrucks
                        .map((assignedTruck) => assignedTruck?.truck?.plateNumber)
                        .filter(Boolean)
                        .join(', ')
                      : t('users.noTruckAssigned')}
                  </p>
                )}
                {accessUser?.role === 'CLIENT' && (
                  <p className={accessClientRowClass}>
                    <span className="font-medium">
                      {t('users.table.assignedClient')}:&nbsp;
                    </span>
                    {accessUser?.clientProfile?.companyName
                      || accessUser?.assignedClient?.companyName
                      || t('users.noneAssigned')}
                  </p>
                )}
                {accessUser?.role === 'ADMIN' && (
                  <p className={accessAdminRowClass}>{t('users.fullAccess')}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <Button type="button" variant="secondary" onClick={onClose}>
                {t('common.close')}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
        {username != null && username !== '' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('login.username')}
            </label>
            <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
              {username}
            </div>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div>
          <Input
            label={t('profile.currentPassword')}
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            minLength={1}
          />
        </div>

        <div>
          <Input
            label={t('profile.newPassword')}
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={6}
          />
        </div>

        <div>
          <Input
            label={t('profile.confirmPassword')}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={6}
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading}
          >
            {t('profile.changePassword')}
          </Button>
        </div>
          </form>
        )}
        <LanguageSwitcher />
      </div>
    </Modal>
  );
};

export default Layout;